import { AwardOrderRequest, OffersToAccept, OrderResponse } from "../../types";

/**
 * Pure award-payload + comparison-matrix formatting for the JOINT (multi-line) award panel.
 *
 * Extracted out of `AwardJoint.tsx` so the AWARD-PAYLOAD PARITY contract can be locked with a
 * unit test: the object handed to `onAcceptOffer` (→ `PUT /offer/list/select`) MUST stay
 * byte-equivalent to the pre-Carbon behaviour. Do not change these shapes without flagging.
 */

export const getRequestedVolumes = (orders: OrderResponse[]) => {
  const requestedVolumes: any = {};
  orders.forEach((order) => {
    requestedVolumes[order?.id] = order?.quantity;
  });
  return requestedVolumes;
};

export const getOldQuantity = (orders: OrderResponse[]) => {
  const requestedVolumes: any = {};
  orders.forEach((order) => {
    requestedVolumes[order?.id] = order?.quantity;
  });
  return requestedVolumes;
};

export const getBestOffersTotal = (formattedOffers: any) => {
  return formattedOffers.reduce(
    (result: { total: number; coin: string | null }, offerGroup: any) => {
      const bestOffers = offerGroup.filter((offer: any) => offer.isBestOffer);

      bestOffers.forEach((bestOffer: any) => {
        result.total +=
          bestOffer?.offer?.price * bestOffer?.offer?.order?.quantity;

        if (!result.coin) {
          result.coin = bestOffer?.offer?.currency || null;
        }
      });

      return result;
    },
    { total: 0, coin: null }
  );
};

export const getPriceByVolume = (offersToAccept: OffersToAccept[]) => {
  let totalPrice = 0;
  let totalVolume = 0;

  offersToAccept.forEach((offer) => {
    if (offer.volume && offer.price) {
      totalPrice += offer.price * offer.volume;
      totalVolume += offer.volume;
    }
  });

  const priceByVolume = totalVolume > 0 ? totalPrice / totalVolume : 0;
  return { totalPrice, totalVolume, priceByVolume };
};

/**
 * Builds the grouped comparison-matrix model (one inner array per supplier), flagging each
 * offer's `isBestOffer` (cheapest total for its order line) and `isBestAccumulate` (supplier
 * group with the lowest accumulated total). Mirrors `AwardJoint.tsx`'s original `formattedOffers`
 * useMemo exactly.
 */
export const formatJointOffers = (offers: [OffersToAccept[]]) => {
  const orderMinPriceMap: any = {};
  const orderAccumulateMap: any = {};
  const arrayAccumulatePrices: any = [];

  Object.values(offers).forEach((offerGroup: OffersToAccept[]) => {
    let groupTotal = 0;

    offerGroup.forEach((offer: any) => {
      const totalPrice = offer?.offer?.price * offer?.offer?.order?.quantity;
      const orderId = offer?.offer?.order?.id;

      if (
        !orderMinPriceMap[orderId] ||
        totalPrice < orderMinPriceMap[orderId]
      ) {
        orderMinPriceMap[orderId] = totalPrice;
      }

      groupTotal += totalPrice;

      if (!orderAccumulateMap[orderId]) {
        orderAccumulateMap[orderId] = 0;
      }
      orderAccumulateMap[orderId] += totalPrice;
    });

    arrayAccumulatePrices.push(groupTotal);
  });

  const minAccumulatedPrice = Math.min(...arrayAccumulatePrices);

  return Object.values(offers).map((offerGroup, index: number) =>
    offerGroup.map((offer) => {
      const totalPrice = offer?.offer?.price * offer?.offer?.order?.quantity;
      const orderId = offer?.offer?.order?.id;
      return {
        ...offer,
        isBestOffer: totalPrice === orderMinPriceMap[orderId],
        isBestAccumulate: arrayAccumulatePrices[index] === minAccumulatedPrice,
        seller: offer?.offer?.user?.company?.name,
      };
    })
  );
};

/**
 * Builds the `AwardOrderRequest[]` passed to `onAcceptOffer`. Byte-equivalent to
 * `AwardJoint.tsx`'s original `onAwardTender` mapping — see AWARD-PAYLOAD PARITY.
 */
export const buildJointAwardPayload = (
  offersToAccept: OffersToAccept[],
  order: OrderResponse[],
  offerSelectionComment: string,
  offerSelectionFileUrl = ""
): AwardOrderRequest[] => {
  const requestedQuantity = getOldQuantity(order);
  return offersToAccept.map((offer) => ({
    offer: {
      ...offer.offer,
      price: parseFloat(String(offer.price)),
      quantity: parseFloat(String(offer.volume)),
      oldQuantity: requestedQuantity[offer.orderId],
      oldPrice: offer.offer.price,
      selected: true,
    },
    offerSelectionComment,
    offerSelectionFileUrl,
    replace: false,
  })) as unknown as AwardOrderRequest[];
};
